/**
 * Configuración del portal, resuelta en **runtime** desde `/config.json`.
 *
 * El stack escribe ese archivo al bucket con sus propios outputs, así que el
 * bundle no sabe a qué ambiente pertenece: el mismo artefacto sirve para TEST
 * y para PROD, y el pipeline puede construirlo una vez y desplegarlo en los
 * dos.
 *
 * Antes estos valores entraban por `import.meta.env` y Vite los horneaba
 * dentro del JS. Eso obligaba a construir un bundle por ambiente —y a hacerlo
 * a mano, porque los valores son outputs de un stack que todavía no
 * desplegó—. Es exactamente lo que prohíben los Estándares COE: un
 * `user_pool_id` literal en el código queda desfasado en cuanto recrean el
 * pool, y el portal se cae hasta que alguien se acuerde de reconstruirlo.
 *
 * El callback de OAuth **no** viene de acá: sale del origen del navegador.
 * Tiene que coincidir con los `callback_urls` del User Pool Client, y el
 * origen desde el que se abrió el portal es literalmente esa URL.
 */
export interface PortalConfig {
  env: string;
  apiUrl: string;
  userPoolId: string;
  userPoolClientId: string;
  /** Vacío = sin login con Google; queda solo email+contraseña. */
  cognitoDomain: string;
}

const REQUERIDAS = ["apiUrl", "userPoolId", "userPoolClientId"] as const;

let actual: PortalConfig | null = null;

/**
 * La config ya cargada. Falla fuerte si se llama antes de `cargarConfig`,
 * porque una request con `undefined` de base sale contra el origen del portal
 * y el 404 se ve como un error del API.
 */
export function config(): PortalConfig {
  if (!actual) throw new Error("config() antes de cargarConfig()");
  return actual;
}

/**
 * Lee `/config.json`. En dev cae a las variables de Vite.
 *
 * CloudFront mapea 403 y 404 a `/index.html` con status **200** para que el
 * router del SPA funcione, así que un `config.json` que falta no llega como
 * error: llega como HTML con un 200 alegre. Por eso no alcanza con mirar
 * `resp.ok` — hay que verificar que lo que volvió tenga forma de config.
 */
export async function cargarConfig(): Promise<PortalConfig> {
  if (actual) return actual;

  let remota: unknown = null;
  try {
    const resp = await fetch("/config.json", { cache: "no-store" });
    if (resp.ok) remota = await resp.json();
  } catch {
    // Sin config.json servido (dev server) o no era JSON (el index.html que
    // devuelve CloudFront). Las dos caen al fallback de abajo.
  }

  const desdeEnv: PortalConfig = {
    env: import.meta.env.VITE_ENV ?? "",
    apiUrl: import.meta.env.VITE_API_URL ?? "",
    userPoolId: import.meta.env.VITE_USER_POOL_ID ?? "",
    userPoolClientId: import.meta.env.VITE_USER_POOL_CLIENT_ID ?? "",
    cognitoDomain: import.meta.env.VITE_COGNITO_DOMAIN ?? "",
  };

  const candidata = (esConfig(remota) ? remota : desdeEnv) as PortalConfig;

  const faltan = REQUERIDAS.filter((k) => !candidata[k]);
  if (faltan.length) {
    throw new Error(
      `El portal no pudo leer su configuración (faltan: ${faltan.join(", ")}). ` +
      "Verificá que /config.json esté publicado en el bucket del portal."
    );
  }

  actual = candidata;
  return actual;
}

function esConfig(v: unknown): v is PortalConfig {
  return !!v && typeof v === "object"
    && REQUERIDAS.every((k) => typeof (v as Record<string, unknown>)[k] === "string"
                              && (v as Record<string, string>)[k] !== "");
}

/** La URL a la que Cognito devuelve tras el login. Es el origen actual, que es
 *  lo que el User Pool Client tiene registrado como callback. */
export function callbackUrl(): string {
  return `${window.location.origin}/`;
}
